import React, { useState, useEffect, useCallback } from 'react';
import { Prompt } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useMappingsByDataType } from 'api/MappingsAPI';
import { GenericDataType } from 'modules/domains';
import { FormField } from 'ui/formField';
import { UnsavedChangesModal } from 'modules/ai/ui/components/UnsavedChanges/UnsavedChangesModal';
import { useUnsavedChangesPrompt } from 'modules/ai/ui/components/UnsavedChanges/useUnsavedChangesModal';
import { useModalContext } from 'utils/contexts/modal';
import { identity } from 'utils/identity';
import { AiColor16 } from 'ui/icons/aurora/16px/AiColor16';
import { QueryBuilder } from 'modules/predictiveGoals/components/queryBuilder';
import { ContentUnavailable } from '../ContentUnavailable';
import { PreviewUsers } from '../previewUsers/PreviewUsers';
import { usePreviewUsers } from '../previewUsers/hooks/usePreviewUsers';
import {
  DEFAULT_DURATION_SERIALIZED,
  DESCRIPTION_CAPTION,
  GOAL_CRITERIA,
  GOAL_DETAILS,
  GOAL_FIELD_NAME,
  GOAL_NAME,
  PG_MODALS,
  PROJECT_VOCABULARY_NOT_READY_HEADER,
  PROVIDE_GOAL_NAME,
  PROVIDE_USER_FIELD,
  TEST_IDS,
} from '../../constants';
import {
  parseEventOptionsFromVocabularyWithOverflow,
  parsePropertyOptionsFromVocabularyWithOverflow,
} from '../../vocabUtils';
import {
  createErrorResultSetter,
  isVocabularyEmpty,
  createValidateCriteriaDebounce,
} from '../../goalUtils';
import {
  ContentUnavailableWrapper,
  DescriptionContainer,
  DetailsInputRow,
  DetailsSectionWrapper,
  GoalBuilderTitleBody,
  GoalBuilderTitleContainer,
  GoalBuilderTitlePreviewContainer,
  GoalCriteriaHeader,
  InputLabel,
  MainContentLayout,
  MainLayoutWrapper,
  StyledInputText,
  StyledQuerySubText,
  StyledUserProfileFieldCaption,
  UpdatedGoalBuilderContainer,
  UpdatedGoalCriteriaHeader,
  UpdatedGoalCriteriaHeaderWrapper,
} from '../Styled';
import { PredictiveGoalQuery, TemplateEventPopoverContent, VocabularyResponse } from '../../types';

export type PredictiveGoalQueryBuilderProps = {
  canEditName: boolean;
  contactProperty: string;
  goalDescription: string;
  goalName: string;
  goalQuery: any;
  hasUnsavedChanges: boolean;
  setContactProperty?: (value: string) => void;
  setGoalDescription: (value: string) => void;
  setGoalName: (value: string) => void;
  setGoalQuery: (goalQuery: any) => void;
  submitButtonClicked: boolean;
  templateEventPopoverContent?: TemplateEventPopoverContent;
  transformQuery?: (query: PredictiveGoalQuery) => PredictiveGoalQuery;
  vocabulary: VocabularyResponse;
};

const PredictiveGoalQueryBuilder = ({
  contactProperty,
  canEditName,
  goalDescription,
  goalName,
  goalQuery,
  hasUnsavedChanges,
  setContactProperty,
  setGoalDescription,
  setGoalName,
  setGoalQuery,
  submitButtonClicked,
  templateEventPopoverContent,
  transformQuery = identity,
  vocabulary,
}: PredictiveGoalQueryBuilderProps) => {
  const [previewButtonDisabled, setPreviewButtonDisabled] = useState(true);
  const navigate = useNavigate();
  const { showModal, hideModal } = useModalContext();
  const { changesConfirmed, confirmChanges, nextLocation, promptForUnsavedChanges } =
    useUnsavedChangesPrompt(showModal, PG_MODALS.UnsavedChanges);
  const debouncedValidator = useCallback(
    createValidateCriteriaDebounce(createErrorResultSetter(setPreviewButtonDisabled)),
    [setPreviewButtonDisabled],
  );
  const {
    isContentReady: isGoalPreviewReady,
    isError: isGoalPreviewError,
    isExtendedLoading,
    isLoading: isGoalPreviewLoading,
    previewUsersCount,
    queryGoalPreview,
  } = usePreviewUsers();

  const { data: userMappings } = useMappingsByDataType(GenericDataType.User);

  useEffect(() => {
    debouncedValidator(goalQuery);
  }, [goalQuery]);
  useEffect(() => {
    if (changesConfirmed && nextLocation) {
      navigate(nextLocation?.pathname || '');
    }
  }, [changesConfirmed, nextLocation]);

  const eventOptions = React.useMemo(
    () => parseEventOptionsFromVocabularyWithOverflow(vocabulary?.predictiveAudiencesVocabulary),
    [vocabulary],
  );
  const propertyOptions = React.useMemo(
    () =>
      parsePropertyOptionsFromVocabularyWithOverflow(
        vocabulary?.predictiveAudiencesVocabulary,
        userMappings,
      ),
    [vocabulary, userMappings],
  );

  const showGoalNameError = submitButtonClicked && !goalName;
  const showContactPropertyError = submitButtonClicked && !contactProperty;
  const isExistingGoal = !setContactProperty;
  const onContactPropertyUpdate = setContactProperty || (() => null);
  const maybeDefaultDuration = DEFAULT_DURATION_SERIALIZED;
  const headerContent = (
    <GoalBuilderTitleContainer>
      <GoalBuilderTitlePreviewContainer>
        <div>
          <GoalBuilderTitleBody>
            Predict likelihood of users in a 30 day period to...
          </GoalBuilderTitleBody>
        </div>
        <PreviewUsers
          displayData={isGoalPreviewReady}
          isDisabled={previewButtonDisabled}
          isError={isGoalPreviewError}
          isExtendedLoading={isExtendedLoading}
          isLoading={isGoalPreviewLoading}
          onClickPreview={() => queryGoalPreview(transformQuery(goalQuery))}
          usersCount={previewUsersCount}
        />
      </GoalBuilderTitlePreviewContainer>
    </GoalBuilderTitleContainer>
  );
  return (
    <MainLayoutWrapper>
      <MainContentLayout>
        <Prompt when={hasUnsavedChanges} message={promptForUnsavedChanges} />
        {hasUnsavedChanges && (
          <UnsavedChangesModal
            modalId={PG_MODALS.UnsavedChanges}
            onClose={hideModal}
            onConfirm={() => {
              confirmChanges();
              hideModal();
            }}
            isDisabled={false}
            resourceName="Predictive Goal"
          />
        )}
        <UpdatedGoalCriteriaHeaderWrapper>
          <UpdatedGoalCriteriaHeader>{GOAL_CRITERIA}</UpdatedGoalCriteriaHeader>
          <AiColor16 />
        </UpdatedGoalCriteriaHeaderWrapper>
        <ContentUnavailableWrapper>
          <ContentUnavailable
            headerMessage={PROJECT_VOCABULARY_NOT_READY_HEADER}
            showPlaceholderContent={isVocabularyEmpty(vocabulary)}
          >
            <UpdatedGoalBuilderContainer>
              {headerContent}
              <QueryBuilder
                id="PredictiveGoalPage"
                onUpdate={setGoalQuery}
                query={goalQuery}
                events={eventOptions}
                properties={propertyOptions}
                defaultDuration={maybeDefaultDuration}
                templatePopoverContent={templateEventPopoverContent}
              />
            </UpdatedGoalBuilderContainer>
          </ContentUnavailable>
        </ContentUnavailableWrapper>
        <GoalCriteriaHeader>{GOAL_DETAILS}</GoalCriteriaHeader>
        <DetailsSectionWrapper>
          {canEditName && (
            <DetailsInputRow>
              <InputLabel>{GOAL_NAME}</InputLabel>
              <FormField errorText={PROVIDE_GOAL_NAME} hasError={showGoalNameError}>
                <StyledInputText
                  data-test={TEST_IDS.CreateGoalNameInput}
                  id="goalNameField"
                  isRequired={true}
                  placeholder="example: Subscription renewal"
                  onChange={setGoalName}
                  type="text"
                  value={goalName}
                />
              </FormField>
            </DetailsInputRow>
          )}
          <DetailsInputRow>
            <InputLabel>
              <span>{GOAL_FIELD_NAME}</span>
            </InputLabel>
            <div>
              <FormField errorText={PROVIDE_USER_FIELD} hasError={showContactPropertyError}>
                <StyledInputText
                  data-test={TEST_IDS.CreateGoalContactPropertyInput}
                  id="userProfileField"
                  isDisabled={isExistingGoal}
                  isRequired={true}
                  onChange={onContactPropertyUpdate}
                  placeholder="example: subscriptionRenewalPropensity"
                  type="text"
                  value={contactProperty}
                />
              </FormField>
              <StyledUserProfileFieldCaption>{DESCRIPTION_CAPTION}</StyledUserProfileFieldCaption>
            </div>
          </DetailsInputRow>
          <DetailsInputRow>
            <DescriptionContainer>
              <span>Description </span>
              <StyledQuerySubText>(optional)</StyledQuerySubText>
            </DescriptionContainer>
            <FormField hasError={false}>
              <StyledInputText
                id="goalDescriptionField"
                isRequired={false}
                onChange={setGoalDescription}
                value={goalDescription}
              />
            </FormField>
          </DetailsInputRow>
        </DetailsSectionWrapper>
      </MainContentLayout>
    </MainLayoutWrapper>
  );
};

export default PredictiveGoalQueryBuilder;
